import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronDown, Sparkles, BookOpen, Compass, ScrollText,
  Heart, Eye, Shield, Waves, Users, Flame, Skull, Mountain, Crown, Orbit,
} from "lucide-react";
import { AcademyIntroPlayer } from "@/components/AcademyIntroPlayer";
import { useT } from "@/i18n/LanguageContext";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { useSiteMedia } from "@/lib/useSiteMedia";
import { STOIC_DISCIPLINES } from "@/lib/disciplines";
import { trackCta } from "@/lib/analytics";
import { TrialCta } from "@/components/academy/TrialGate";
import { useAuth } from "@/context/AuthContext";

/**
 * Academy of Wisdom — the philosophical heart of StoicOs.ai.
 *
 * Not a course catalogue, not an e-learning product — a lifelong
 * academy where the curriculum is life itself and Virtue Guide is
 * the personal mentor. Positioned as: Oxford + Harvard + Ancient
 * Athens, timeless (not dusty), Apple-level polish.
 *
 * Sections (top → bottom):
 *   Cinematic Hero (the official entrance — image only, no overlay text)
 *   → The Academy Introduction (video placeholder) → Why → How it Works
 *   → First Curriculum → Meet Your Mentor → Future Academy → Closing
 *
 * Reveals-on-scroll (IntersectionObserver) mirror the pattern
 * established by MissionSection so the whole homepage / academy
 * page feels visually coherent. Reduced-motion is respected.
 */

// One-off IO reveal helper — kept local to this file (single caller).
function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setSeen(true); io.disconnect(); }
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
}

function Reveal({ children, delay = 0, className = "", testid }) {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      data-testid={testid}
      className={`transition-all duration-1000 ease-out ${
        seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      } ${className}`}
      style={{ transitionDelay: seen ? `${delay}ms` : "0ms" }}
    >
      {children}
    </div>
  );
}

// Thirteen curriculum doors. Each door tells three stories:
//   1. The Door        — modern English title (immediately understood)
//   2. The Classical   — the original Greek/Latin philosophical concept
//   3. The Promise     — one sentence stating what the student will learn
// The classical term is deliberately quiet: smaller, italic, lighter
// gold. It communicates the philosophical foundation without competing
// with the title. Outside: clarity. Inside: depth.
const CURRICULUM = [
  { id: "welcome",        Icon: Sparkles,   titleKey: "academy.card.welcome",        classicalKey: "academy.card.welcomeClassical",        subKey: "academy.card.welcomeSub" },
  { id: "lifeCurriculum", Icon: BookOpen,   titleKey: "academy.card.lifeCurriculum", classicalKey: "academy.card.lifeCurriculumClassical", subKey: "academy.card.lifeCurriculumSub" },
  { id: "knowThyself",    Icon: Eye,        titleKey: "academy.card.knowThyself",    classicalKey: "academy.card.knowThyselfClassical",    subKey: "academy.card.knowThyselfSub" },
  { id: "fourVirtues",    Icon: Compass,    titleKey: "academy.card.fourVirtues",    classicalKey: "academy.card.fourVirtuesClassical",    subKey: "academy.card.fourVirtuesSub" },
  { id: "emotions",       Icon: Heart,      titleKey: "academy.card.emotions",       classicalKey: "academy.card.emotionsClassical",       subKey: "academy.card.emotionsSub" },
  { id: "judgement",      Icon: ScrollText, titleKey: "academy.card.judgement",      classicalKey: "academy.card.judgementClassical",      subKey: "academy.card.judgementSub" },
  { id: "selfMastery",    Icon: Shield,     titleKey: "academy.card.selfMastery",    classicalKey: "academy.card.selfMasteryClassical",    subKey: "academy.card.selfMasterySub" },
  { id: "fortuna",        Icon: Waves,      titleKey: "academy.card.fortuna",        classicalKey: "academy.card.fortunaClassical",        subKey: "academy.card.fortunaSub" },
  { id: "relationships",  Icon: Users,      titleKey: "academy.card.relationships",  classicalKey: "academy.card.relationshipsClassical",  subKey: "academy.card.relationshipsSub" },
  { id: "purpose",        Icon: Flame,      titleKey: "academy.card.purpose",        classicalKey: "academy.card.purposeClassical",        subKey: "academy.card.purposeSub" },
  { id: "death",          Icon: Skull,      titleKey: "academy.card.death",          classicalKey: "academy.card.deathClassical",          subKey: "academy.card.deathSub" },
  { id: "resilience",     Icon: Mountain,   titleKey: "academy.card.resilience",     classicalKey: "academy.card.resilienceClassical",     subKey: "academy.card.resilienceSub" },
  { id: "leadership",     Icon: Crown,      titleKey: "academy.card.leadership",     classicalKey: "academy.card.leadershipClassical",     subKey: "academy.card.leadershipSub" },
];

// The official Academy Introduction film — hosted on Emergent Object Storage,
// served via our backend, played in our own black/gold player.
const INTRO_VIDEO_SRC = `${process.env.REACT_APP_BACKEND_URL}/api/media/academy-introduction.mp4`;
const INTRO_VIDEO_POSTER = "/academy-hero.jpg";

// The Journey — six movements from lived moment to wisdom.
const JOURNEY_STEPS = [
  "academy.how.step.experience",
  "academy.how.step.reflection",
  "academy.how.step.understanding",
  "academy.how.step.virtue",
  "academy.how.step.character",
  "academy.how.step.wisdom",
];

// The Three Disciplines of Stoicism (Founder decision, 2026-07) — the
// classical architecture of the philosophy, presented BEFORE the First
// Curriculum: Logic → Physics → Ethics. Ids/numerals come from the
// canonical taxonomy (src/lib/disciplines.js); icons are presentation only.
const DISCIPLINE_ICONS = { logic: ScrollText, physics: Orbit, ethics: Compass };
const DISCIPLINES = STOIC_DISCIPLINES.map((d) => ({ ...d, Icon: DISCIPLINE_ICONS[d.id] }));

// Future subjects — flat list rendered as gold-hairlined pills.
const FUTURE_SUBJECTS = [
  "academy.future.wisdom", "academy.future.justice", "academy.future.courage", "academy.future.temperance",
  "academy.future.purpose", "academy.future.love", "academy.future.leadership", "academy.future.finance",
  "academy.future.parenthood", "academy.future.health", "academy.future.legacy", "academy.future.death",
  "academy.future.meaning", "academy.future.modernLife",
];

export default function Academy() {
  const { t, lang, setLang } = useT();

  useSEO({
    title: t("academy.hero.title") + " · StoicOs.ai",
    description: `${t("academy.hero.subtitle.lead")} ${t("academy.hero.subtitle.accent")}`,
    canonical: langCanonical("/academy", lang),
    alternates: langAlternates("/academy"),
  });

  // Reuse the Marcus portrait for the mentor section — same slot the
  // homepage uses so the founder assigns one image and it appears
  // everywhere. Later, a dedicated `academy.mentor.portrait` slot
  // can be added; for now we don't invent new slots without need.
  const mentorMedia = useSiteMedia("home.guide.marcus", {
    fallbackUrl: "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/4h3m97yf_IMG_2514.png",
    fallbackAlt: "Virtue Guide, the Academy's personal mentor",
  });

  // eslint-disable-next-line no-unused-vars
  const handleBegin = (label) => (e) => {
    trackCta(label);
    const target = document.getElementById("download");
    if (target) { e.preventDefault(); target.scrollIntoView({ behavior: "smooth", block: "start" }); }
  };

  const { user } = useAuth();
  // Existing students (or logged-in users) go straight to their personal
  // My StoicOs; everyone else lands in the existing register/login flow.
  const studentLinkTo = user ? "/my-stoicos" : "/my-stoicos?mode=login";

  return (
    <main data-testid="academy-page" className="bg-ink text-bone overflow-hidden">

      {/* =============================================================
          HERO — the official entrance to the Academy.
          Two artworks, one identity (Architect's decision):
            • ≥768px  → landscape hero  (/academy-hero.jpg)
            • <768px  → portrait hero   (/academy-hero-mobile.jpg)
          Selected automatically via <picture> media queries. NEITHER
          artwork is cropped: object-contain inside a full-viewport
          black stage — the artworks' dark edges blend seamlessly.
          No overlay text, no buttons; slow Ken Burns; bottom fade;
          quiet gold chevron invites scroll.
          ============================================================= */}
      <section
        data-testid="academy-hero"
        className="relative w-full h-[100svh] min-h-[480px] overflow-hidden bg-black"
      >
        <h1 className="sr-only">{t("academy.hero.title")}</h1>
        <div className="academy-hero-kenburns absolute inset-0 pt-12 md:pt-0">
          <picture className="block w-full h-full">
            <source media="(min-width: 768px)" srcSet="/academy-hero.jpg" />
            <img
              src="/academy-hero-mobile.jpg"
              alt={t("academy.hero.alt")}
              data-testid="academy-hero-image"
              className="w-full h-full object-contain object-center"
            />
          </picture>
        </div>
        {/* Top scrim — keeps the fixed navigation elegant and readable */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-32
          bg-gradient-to-b from-black/55 to-transparent" />
        {/* Bottom fade — dissolves the hero into the page's ink black.
            Softer on mobile so the StoicOs.ai mark in the artwork stays visible. */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-24 md:h-56
          bg-gradient-to-t from-ink/85 md:from-ink via-ink/40 md:via-ink/60 to-transparent" />
        {/* Trial CTA — the Academy page is the public showcase; the full
            studies happen inside My StoicOs (existing flow, no new logic). */}
        <div className="absolute bottom-16 md:bottom-24 inset-x-0 flex flex-col items-center gap-4 px-6 text-center">
          <h2
            data-testid="academy-hero-cta-headline"
            className="font-serif text-2xl md:text-4xl text-bone leading-tight [text-shadow:0_2px_16px_rgba(0,0,0,0.85)]"
          >
            {t("trial.hero.headline")}
          </h2>
          <p
            data-testid="academy-hero-cta-subtitle"
            className="max-w-xl text-sm md:text-base text-bone/85 font-reading leading-[1.8] [text-shadow:0_1px_10px_rgba(0,0,0,0.85)]"
          >
            {t("trial.hero.subtitle")}
          </p>
          <TrialCta testid="academy-hero-trial-cta" onClickTrack={() => trackCta("academy_hero_trial")} />
          <Link
            to={studentLinkTo}
            data-testid="academy-hero-student-link"
            onClick={() => trackCta("academy_hero_student_login")}
            className="text-sm text-bone/80 hover:text-gold underline underline-offset-4 transition-colors [text-shadow:0_1px_8px_rgba(0,0,0,0.8)]"
          >
            {t("trial.hero.secondary")}
          </Link>
        </div>
        {/* Scroll indicator */}
        <div
          data-testid="academy-hero-scroll-cue"
          className="absolute bottom-6 md:bottom-8 inset-x-0 flex justify-center"
        >
          <ChevronDown size={26} strokeWidth={1.25} className="scroll-cue-arrow text-gold" aria-hidden />
        </div>
      </section>

      {/* =============================================================
          THE ACADEMY INTRODUCTION — vision video (placeholder for now)
          ============================================================= */}
      <section data-testid="academy-intro" className="relative py-24 md:py-36 px-6 sm:px-10">
        <Reveal className="relative max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="h-px w-12 bg-gold/60" aria-hidden />
            <span data-testid="academy-intro-title" className="font-serif text-2xl md:text-4xl text-bone tracking-tight">
              {t("academy.intro.title")}
            </span>
            <span className="h-px w-12 bg-gold/60" aria-hidden />
          </div>
          <p className="font-serif text-lg md:text-2xl text-bone/85 leading-relaxed">
            {t("academy.intro.sub1")}
            <br />
            <span className="italic text-gold">{t("academy.intro.sub2")}</span>
          </p>
        </Reveal>

        <Reveal delay={140} className="relative max-w-4xl mx-auto mt-14 md:mt-16">
          <AcademyIntroPlayer src={INTRO_VIDEO_SRC} poster={INTRO_VIDEO_POSTER} />
        </Reveal>

        <Reveal delay={220} className="relative max-w-2xl mx-auto mt-12 md:mt-16 text-center">
          <p data-testid="academy-intro-quote" className="font-serif italic text-lg md:text-2xl leading-relaxed text-bone/85">
            {t("academy.intro.quote1")}
            <br />
            <span className="text-gold">{t("academy.intro.quote2")}</span>
          </p>
        </Reveal>
      </section>

      {/* =============================================================
          SECTION 1 — Why another Academy
          ============================================================= */}
      <section data-testid="academy-why" className="relative py-28 md:py-40 px-6 sm:px-10">
        <Reveal className="relative max-w-3xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-8">
            {t("academy.why.eyebrow")}
          </p>
          <div className="space-y-6 font-serif text-2xl md:text-3xl leading-[1.5] text-bone/90">
            <p>{t("academy.why.p1")}</p>
            <p>{t("academy.why.p2")}</p>
          </div>
          <div className="gold-rule mx-auto w-32 my-14" aria-hidden />
          <div className="space-y-5 font-serif text-2xl md:text-3xl leading-[1.5] text-bone">
            <p>{t("academy.why.p3")}</p>
            <p>
              {t("academy.why.p4.lead")}{" "}
              <span className="italic text-gold">{t("academy.why.p4.accent")}</span>
            </p>
          </div>
          <p className="mt-14 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-xl mx-auto">
            {t("academy.why.p5")}
          </p>
        </Reveal>
      </section>

      {/* =============================================================
          SECTION 2 — How it Works (journey)
          ============================================================= */}
      <section data-testid="academy-how" className="relative py-28 md:py-40 px-6 sm:px-10 border-t border-white/5">
        <Reveal className="relative max-w-4xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-6">
            {t("academy.how.eyebrow")}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-bone leading-[1.15] tracking-tight max-w-2xl mx-auto">
            {t("academy.how.title")}
          </h2>
          <p className="mt-8 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-2xl mx-auto">
            {t("academy.how.body")}
          </p>
        </Reveal>

        <Reveal delay={120} className="relative max-w-3xl mx-auto mt-20">
          <ol
            data-testid="academy-journey"
            className="flex flex-col items-center gap-6"
          >
            {JOURNEY_STEPS.map((k, i) => {
              const isLast = i === JOURNEY_STEPS.length - 1;
              return (
                <li key={k} className="flex flex-col items-center gap-4">
                  <div
                    data-testid={`academy-journey-step-${i}`}
                    className={`px-8 py-4 border ${
                      isLast ? "border-gold/60 bg-gold/5 text-gold" : "border-white/12 text-bone/90"
                    } font-inscription text-base md:text-lg tracking-[0.28em] uppercase transition-colors`}
                  >
                    {t(k)}
                  </div>
                  {!isLast && (
                    <div className="w-px h-6 bg-gradient-to-b from-gold/60 to-transparent" aria-hidden />
                  )}
                </li>
              );
            })}
          </ol>
        </Reveal>
      </section>

      {/* =============================================================
          THE MAP OF PRACTICAL WISDOM — the approved Founder artwork,
          EN/NL switched by the active site language. Static, no zoom,
          no parallax, no hotspots (Founder directive).
          ============================================================= */}
      <section data-testid="academy-map" className="relative py-28 md:py-40 px-6 sm:px-10 border-t border-white/5">
        <Reveal className="relative max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-bone leading-[1.15] tracking-tight max-w-2xl mx-auto">
            {t("academy.map.title")}
          </h2>
          <p className="mt-8 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-2xl mx-auto">
            {t("academy.map.p1")}
          </p>
          <p className="mt-5 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-2xl mx-auto">
            {t("academy.map.p2")}
          </p>
          <p className="mt-5 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-2xl mx-auto">
            {t("academy.map.p3")}
          </p>
        </Reveal>

        <Reveal delay={120} className="relative max-w-[1300px] mx-auto mt-16 md:mt-24">
          <img
            data-testid="academy-map-image"
            src={lang === "nl" ? "/map-of-practical-wisdom-nl.jpg" : "/map-of-practical-wisdom-en.jpg"}
            alt={t("academy.map.alt")}
            className="block w-full h-auto mx-auto rounded-sm border border-gold/30 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.85)]"
            loading="lazy"
          />
        </Reveal>

        <Reveal delay={160} className="relative max-w-2xl mx-auto mt-12 md:mt-16 text-center">
          <p data-testid="academy-map-note-label" className="text-[10px] tracking-[0.32em] uppercase text-gold/70">
            {t("academy.map.note.label")}
          </p>
          <p data-testid="academy-map-note-text" className="mt-4 font-serif italic text-sm md:text-base text-fog/85 leading-[1.85]">
            {t("academy.map.note.text")}
          </p>
        </Reveal>
      </section>

      {/* =============================================================
          THE THREE DISCIPLINES OF STOICISM — the architecture of the
          philosophy, before the curriculum. Logic → Physics → Ethics.
          ============================================================= */}
      <section data-testid="academy-disciplines" className="relative py-28 md:py-40 px-6 sm:px-10 border-t border-white/5">
        {/* Curriculum catcher — public preview, studies inside My StoicOs */}
        <Reveal className="relative max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <p data-testid="academy-curriculum-catcher" className="font-reading italic text-base md:text-lg text-fog leading-[1.9]">
            {t("trial.academy.note")}
          </p>
        </Reveal>
        <Reveal className="relative max-w-4xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-6">
            {t("academy.disc.eyebrow")}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-bone leading-[1.15] tracking-tight max-w-2xl mx-auto">
            {t("academy.disc.title")}
          </h2>
          <p className="mt-8 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-2xl mx-auto">
            {t("academy.disc.body")}
          </p>
        </Reveal>

        {/* The editorial concept — three inscriptions, one conclusion */}
        <Reveal delay={120} className="relative max-w-3xl mx-auto mt-16 md:mt-20 text-center">
          <div data-testid="academy-disciplines-concept" className="space-y-3.5">
            <p className="font-serif text-xl md:text-2xl text-bone/90 leading-relaxed">{t("academy.disc.concept.logic")}</p>
            <p className="font-serif text-xl md:text-2xl text-bone/90 leading-relaxed">{t("academy.disc.concept.physics")}</p>
            <p className="font-serif text-xl md:text-2xl text-bone/90 leading-relaxed">{t("academy.disc.concept.ethics")}</p>
          </div>
          <div className="gold-rule mx-auto w-24 my-8" aria-hidden />
          <p className="font-serif italic text-xl md:text-2xl text-gold leading-relaxed">
            {t("academy.disc.concept.close")}
          </p>
        </Reveal>

        {/* Three premium tiles — Logic, Physics, Ethics */}
        <div className="max-w-6xl mx-auto mt-14 flex justify-center"><DisciplineSwitcher active={null} /></div>
        {/* Toelichting: lessen worden binnen My StoicOs gevolgd (gratis account) */}
        <Reveal className="relative max-w-2xl mx-auto mt-14 text-center" testid="academy-disciplines-mystoicos-note">
          <h3 className="font-serif text-2xl md:text-3xl text-bone leading-tight">{t("academy.disc.mystoicos.title")}</h3>
          <p className="mt-4 text-sm md:text-[15px] text-fog font-reading leading-[1.85]">{t("academy.disc.mystoicos.text")}</p>
        </Reveal>
        <div className="relative max-w-6xl mx-auto mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {DISCIPLINES.map((d, idx) => {
            const Icon = d.Icon;
            const isOpen = true;
            const Tile = "div";
            const tileProps = {};
            return (
              <Reveal key={d.id} delay={idx * 80} testid={`academy-discipline-${d.id}`}>
                <Tile {...tileProps} className={`group relative flex flex-col h-full border bg-white/[0.015] hover:bg-gold/[0.03] p-8 md:p-10 transition-all duration-500 ${d.id === "physics" ? "border-[#7fb69a]/45 shadow-[0_0_28px_rgba(127,182,154,0.22)] hover:border-[#7fb69a]" : d.id === "ethics" ? "border-[#c86058]/45 shadow-[0_0_28px_rgba(200,96,88,0.22)] hover:border-[#c86058]" : "border-[#8fa3c8]/45 shadow-[0_0_28px_rgba(143,163,200,0.22)] hover:border-[#8fa3c8]"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <Icon size={24} strokeWidth={1.3} className="text-gold/80 group-hover:text-gold transition-colors" />
                    <span className="font-serif text-lg text-gold/40 group-hover:text-gold/70 transition-colors" aria-hidden>
                      {d.numeral}
                    </span>
                  </div>
                  <span
                    data-testid={`academy-discipline-${d.id}-status`}
                    className={`mt-6 self-start text-[10px] tracking-[0.28em] uppercase px-2.5 py-1 border ${isOpen ? "border-gold/60 text-gold bg-gold/10" : "border-white/15 text-bone/40"}`}
                  >
                    {isOpen ? t("academy.disc.status.open") : t("academy.disc.status.preparation")}
                  </span>
                  <h3 className="mt-5 font-serif text-2xl md:text-[1.7rem] text-bone leading-tight">
                    {t(`academy.disc.${d.id}.title`)}
                  </h3>
                  <p className="mt-2 font-serif italic text-base text-gold/80 leading-snug">
                    {t(`academy.disc.${d.id}.subtitle`)}
                  </p>
                  <p className="mt-1 font-serif italic text-[13px] text-gold/45 leading-none tracking-wide">
                    {t(`academy.disc.${d.id}.classical`)}
                  </p>
                  <p className="mt-6 text-sm md:text-[15px] text-fog font-reading leading-[1.85]">
                    {t(`academy.disc.${d.id}.body`)}
                  </p>
                  <div className="mt-8 pt-6 border-t border-white/8 flex-1">
                    <p className="text-[10px] tracking-[0.32em] uppercase text-bone/40 mb-4">
                      {t("academy.disc.topicsLabel")}
                    </p>
                    <ul className="space-y-2">
                      {t(`academy.disc.${d.id}.topics`).split("|").map((topic) => (
                        <li key={topic} className="flex items-baseline gap-2.5 text-[13px] text-bone/70 font-reading leading-relaxed">
                          <span className="h-px w-3 shrink-0 bg-gold/50 translate-y-[-3px]" aria-hidden />
                          {topic}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {isOpen && (
                    <div className={`mt-8 pt-5 border-t ${d.id === "physics" ? "border-[#7fb69a]/25" : d.id === "ethics" ? "border-[#c86058]/25" : "border-[#8fa3c8]/25"} space-y-4`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[10px] tracking-[0.28em] uppercase text-bone/40">{lang === "nl" ? "Taal" : "Language"}</span>
                        <div role="group" aria-label={`Course language for ${t(`academy.disc.${d.id}.title`)}`}
                          className="inline-flex border border-white/15" data-testid={`academy-${d.id}-lang-selector`}>
                          {["en", "nl"].map((lng) => (
                            <button key={lng} type="button"
                              data-testid={`academy-${d.id}-lang-${lng}`}
                              aria-pressed={lang === lng}
                              onClick={(e) => { e.stopPropagation(); setLang(lng); }}
                              className={`min-w-[44px] min-h-[36px] px-3 text-[11px] tracking-[0.2em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70 ${lang === lng
                                ? (d.id === "physics" ? "bg-[#2e5040] text-[#eaf5ee]" : d.id === "ethics" ? "bg-[#6b2a2a] text-[#f5e4e4]" : "bg-[#2e3a50] text-[#e8edf5]")
                                : "text-bone/50 hover:text-bone"}`}>
                              {lng.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                      <Link to={user ? `/academy/${d.id}` : "/my-stoicos"}
                        data-testid={`academy-discipline-${d.id}-link`}
                        onClick={() => {
                          if (!user) sessionStorage.setItem("postAuthRedirect", `/academy/${d.id}`);
                          trackCta(`academy_discipline_${d.id}_open`);
                        }}
                        className={`flex items-center justify-center gap-2.5 w-full min-h-[52px] px-6 text-[12px] font-semibold tracking-[0.26em] uppercase transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-ink ${d.id === "physics"
                          ? "bg-[#2e5040] text-[#eaf5ee] hover:brightness-125 active:brightness-90 focus-visible:ring-[#7fb69a] shadow-[0_0_24px_rgba(127,182,154,0.15)]"
                          : d.id === "ethics"
                          ? "bg-[#6b2a2a] text-[#f5e4e4] hover:brightness-125 active:brightness-90 focus-visible:ring-[#c86058] shadow-[0_0_24px_rgba(200,96,88,0.15)]"
                          : "bg-[#2e3a50] text-[#e8edf5] hover:brightness-125 active:brightness-90 focus-visible:ring-[#8fa3c8] shadow-[0_0_24px_rgba(143,163,200,0.15)]"}`}>
                        {t(`academy.disc.start.${d.id}`)}
                        <ArrowRight size={15} strokeWidth={2} aria-hidden />
                      </Link>
                      <p data-testid={`academy-discipline-${d.id}-account-note`}
                        className="text-center text-[10px] tracking-[0.24em] uppercase text-bone/35">
                        {t("academy.disc.freeAccount")}
                      </p>
                    </div>
                  )}
                </Tile>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* =============================================================
          SECTION 2b — THE LIBRARY (moved here from the Agora, 2026-06)
          ============================================================= */}
      <section data-testid="academy-library" className="relative py-28 md:py-40 px-6 sm:px-10 border-t border-white/5">
        <Reveal className="relative max-w-3xl mx-auto">
          <h2 data-testid="academy-library-title" className="font-serif text-3xl md:text-5xl text-bone leading-[1.15] tracking-tight">
            {t("agora.s7.title")}
          </h2>
          <p className="mt-8 font-serif italic text-gold/80 text-lg leading-[1.6]">{t("agora.s7.atmosphere")}</p>
          <p className="mt-8 font-reading text-bone/80 text-lg leading-[1.85]">{t("agora.s7.body")}</p>
          <ul className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-4">
            {["agora.s7.shelf1", "agora.s7.shelf2", "agora.s7.shelf3", "agora.s7.shelf4", "agora.s7.shelf5"].map((k) => (
              <li
                key={k}
                data-testid={`academy-library-shelf-${k.split(".").pop()}`}
                className="px-4 py-6 border border-white/10 text-center text-[11px] tracking-[0.22em] uppercase text-bone/80 bg-white/[0.015]"
              >
                {t(k)}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[11px] tracking-[0.22em] uppercase text-gold/70">{t("agora.placeholder")}</p>
        </Reveal>
      </section>

      {/* =============================================================
          SECTION 3 — The First Curriculum (12 cards)
          ============================================================= */}
      <section data-testid="academy-curriculum" className="relative py-28 md:py-40 px-6 sm:px-10 border-t border-white/5">
        <Reveal className="relative max-w-4xl mx-auto text-center mb-16">
          <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-6">
            {t("academy.curriculum.eyebrow")}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-bone leading-[1.15] tracking-tight max-w-2xl mx-auto">
            {t("academy.curriculum.title")}
          </h2>
          <p className="mt-8 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-2xl mx-auto">
            {t("academy.curriculum.body")}
          </p>
        </Reveal>

        <div className="relative max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {CURRICULUM.map((c, idx) => {
            const Icon = c.Icon;
            return (
              <Reveal key={c.id} delay={idx * 40} testid={`academy-card-${c.id}`}>
                <div className="group relative h-full border border-white/10 hover:border-gold/50 bg-white/[0.015] hover:bg-gold/[0.03] p-8 transition-all duration-500 cursor-default">
                  <div className="flex items-start justify-between gap-4">
                    <Icon size={22} strokeWidth={1.4} className="text-gold/80 group-hover:text-gold transition-colors" />
                    <span className="text-[9px] tracking-[0.28em] uppercase text-bone/30 group-hover:text-gold/60 transition-colors">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-8 font-serif text-xl md:text-2xl text-bone leading-tight">
                    {t(c.titleKey)}
                  </h3>
                  {/*
                    The Classical Principle — the philosophical foundation
                    beneath the modern English title. Small, italic, in a
                    quieter gold so the door title stays the primary
                    visual element. Outside: clarity. Inside: depth.
                  */}
                  <p className="mt-1.5 font-serif italic text-[13px] md:text-sm text-gold/55 leading-none tracking-wide">
                    {t(c.classicalKey)}
                  </p>
                  <p className="mt-4 text-sm text-fog font-reading leading-relaxed">
                    {t(c.subKey)}
                  </p>
                  <div className="mt-8 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-bone/40 group-hover:text-gold transition-colors">
                    {t("academy.card.openPath")}
                    <ArrowRight size={11} strokeWidth={1.75} />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* =============================================================
          SECTION 4 — Meet Your Mentor
          ============================================================= */}
      <section data-testid="academy-mentor" className="relative py-28 md:py-40 px-6 sm:px-10 border-t border-white/5 overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-70"
          style={{ background: "radial-gradient(50% 50% at 30% 50%, rgba(232,200,121,0.06) 0%, rgba(0,0,0,0) 60%)" }} />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24 items-center">

          <Reveal testid="academy-mentor-portrait" className="relative">
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto border border-gold/25 bg-black/50 overflow-hidden">
              <img
                src={mentorMedia.url}
                alt={mentorMedia.alt}
                loading="lazy"
                data-media-source={mentorMedia.source || "legacy"}
                className="block w-full h-full object-cover"
              />
              <div aria-hidden className="absolute inset-0 pointer-events-none"
                style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)" }} />
            </div>
          </Reveal>

          <Reveal delay={120} className="relative">
            <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-6">
              {t("academy.mentor.eyebrow")}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-bone leading-[1.1] tracking-tight">
              {t("academy.mentor.title.lead")}{" "}
              <span className="italic text-gold">{t("academy.mentor.title.accent")}</span>
            </h2>
            <div className="mt-10 space-y-6 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-lg">
              <p>{t("academy.mentor.body1")}</p>
              <p className="font-inscription tracking-[0.22em] text-bone/85 uppercase text-sm">
                {t("academy.mentor.body2")}
              </p>
              <p>{t("academy.mentor.body3")}</p>
            </div>
            <div className="mt-10 flex flex-col gap-1">
              <p className="font-inscription tracking-[0.24em] uppercase text-gold text-base md:text-lg">
                {t("academy.mentor.always1")}
              </p>
              <p className="font-inscription tracking-[0.24em] uppercase text-bone text-base md:text-lg">
                {t("academy.mentor.always2")}
              </p>
            </div>
            <div className="mt-12">
              <Link
                to="/ai-stoic-coach"
                onClick={() => trackCta("academy_mentor_open")}
                data-testid="academy-mentor-cta"
                className="inline-flex items-center gap-3 px-8 py-4 border border-gold/60 text-gold hover:bg-gold hover:text-ink text-[11px] tracking-[0.32em] uppercase font-semibold transition-all duration-500"
              >
                {t("academy.mentor.cta")}
                <ArrowRight size={14} strokeWidth={2} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =============================================================
          SECTION 5 — Future Academy
          ============================================================= */}
      <section data-testid="academy-future" className="relative py-28 md:py-40 px-6 sm:px-10 border-t border-white/5">
        <Reveal className="relative max-w-4xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-6">
            {t("academy.future.eyebrow")}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-bone leading-[1.15] tracking-tight max-w-2xl mx-auto">
            {t("academy.future.title")}
          </h2>
          <p className="mt-8 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-2xl mx-auto">
            {t("academy.future.body")}
          </p>
        </Reveal>

        <Reveal delay={120} className="relative max-w-4xl mx-auto mt-16">
          <ul
            data-testid="academy-future-subjects"
            className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3"
          >
            {FUTURE_SUBJECTS.map((k) => (
              <li key={k}>
                <span className="inline-flex items-center px-4 py-2 border border-white/12 hover:border-gold/50 text-bone/85 hover:text-gold text-[10px] md:text-[11px] tracking-[0.28em] uppercase transition-colors">
                  {t(k)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-14 text-center text-sm text-fog italic font-serif">
            {t("academy.future.footer")}
          </p>
        </Reveal>
      </section>

      {/* =============================================================
          STUDY GUIDE — The Student Compass (future free download / lead magnet)
          ============================================================= */}
      <section data-testid="academy-study-guide" className="relative py-24 md:py-32 px-6 sm:px-10 border-t border-white/5">
        <Reveal className="relative max-w-3xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-6">Study Guide</p>
          <h2 className="font-serif text-2xl md:text-4xl text-bone leading-[1.2]">The Student Compass</h2>
          <p className="mt-3 font-serif italic text-lg md:text-xl text-bone/70">Your Guide to the Academy of Practical Wisdom</p>
          <p className="mt-8 text-base text-fog font-reading leading-[1.9] max-w-xl mx-auto">
            A companion that walks you through the three disciplines and how to study
            them — soon available here as a free download.
          </p>
        </Reveal>
      </section>

      {/* =============================================================
          CLOSING
          ============================================================= */}
      <section data-testid="academy-closing" className="relative py-32 md:py-44 px-6 sm:px-10 border-t border-white/5">
        <Reveal className="relative max-w-3xl mx-auto text-center">
          <p className="font-serif text-2xl md:text-4xl leading-[1.25] text-bone/90">
            Reading about wisdom is the beginning.
          </p>
          <p className="mt-4 font-serif text-2xl md:text-4xl italic leading-[1.25] text-gold">
            Practicing it changes your life.
          </p>
          <p className="mt-10 text-base md:text-lg text-fog font-reading leading-[1.9] max-w-xl mx-auto">
            Begin your studies, keep your Stoic Journal, follow your progress and
            receive guidance from the Sages inside My StoicOs.
          </p>
          <div className="gold-rule mx-auto w-32 my-14" aria-hidden />
          <TrialCta testid="academy-closing-cta" onClickTrack={() => trackCta("academy_closing_trial")} />
        </Reveal>
      </section>
    </main>
  );
}
